import { useCallback, useState } from 'react';
import { MediaItem } from '../media.types';
import { message } from '../../message';
import { useResponsive } from 'app/hooks';

type DownloadStatus = 'idle' | 'downloading' | 'success' | 'error';

export type DownloadProgress = Record<
  string,
  { status: DownloadStatus; progress: number }
>;

export const useFileDownload = () => {
  const [progress, setProgress] = useState<DownloadProgress>({});
  const { device } = useResponsive();

  const download = useCallback(async (file: MediaItem) => {
    if (device.phone || device.tablet || device.touch) {
      const link = document.createElement('a');
      link.href = file.src;
      link.download = file.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return;
    }

    setProgress((prev) => ({
      ...prev,
      [file.id]: { status: 'downloading', progress: 0 },
    }));

    try {
      const response = await fetch(file.src);

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      // Fallback: если ReadableStream не поддерживается — качаем через blob сразу
      if (!response.body) {
        const blob = await response.blob();
        triggerDownload(blob, file.name);
        setProgress((prev) => ({
          ...prev,
          [file.id]: { status: 'success', progress: 100 },
        }));

        return;
      }

      const reader = response.body.getReader();
      const contentLength = Number(response.headers.get('Content-Length')) || 0;

      let receivedLength = 0;
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        if (value) {
          chunks.push(value);
          receivedLength += value.length;

          const currentProgress = contentLength
            ? (receivedLength / contentLength) * 100
            : 0;

          setProgress((prev) => ({
            ...prev,
            [file.id]: {
              status: 'downloading',
              progress: currentProgress,
            },
          }));
        }
      }

      const blob = new Blob(chunks.map((chunk) => chunk.slice().buffer));
      triggerDownload(blob, file.name);
      setProgress((prev) => ({
        ...prev,
        [file.id]: { status: 'success', progress: 100 },
      }));
    } catch (error) {
      message({ type: 'error', text: 'Не удалось скачать файл' });
      setProgress((prev) => ({
        ...prev,
        [file.id]: { status: 'error', progress: 0 },
      }));
    }
  }, []);

  return { call: download, progress };
};

const triggerDownload = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
